import { Link } from "react-router-dom";
import { Rating, BenefitList, BuyButton } from "@/components/ProductCard";
import { Trophy } from "@phosphor-icons/react";

export function TopPickRow({ product, rank }) {
  return (
    <article
      data-testid={`top-pick-${rank}`}
      className="hh-rise group grid grid-cols-[104px_1fr] sm:grid-cols-[140px_1fr] gap-4 sm:gap-6 rounded-2xl bg-white border border-[#E5E0D8] p-3 sm:p-4 hover:shadow-[0_14px_40px_rgba(44,43,41,0.10)] transition-shadow duration-300"
      style={{ animationDelay: `${rank * 60}ms` }}
    >
      <Link to={`/product/${product.id}`} className="relative block aspect-square rounded-xl overflow-hidden bg-[#EADDC8]/30">
        <img src={product.image_url} alt={product.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <span className={`absolute top-2 left-2 text-xs font-bold px-2 py-0.5 rounded-full ${rank <= 3 ? "bg-[#D17A58] text-white" : "bg-[#2C2B29] text-white"}`}>
          #{rank}
        </span>
      </Link>
      <div className="flex flex-col min-w-0">
        {rank === 1 && (
          <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.16em] text-[#D17A58] font-semibold mb-1">
            <Trophy size={13} weight="fill" /> Best overall
          </span>
        )}
        <span className="text-[11px] uppercase tracking-wider text-[#5B7B53]">{product.category}</span>
        <Link to={`/product/${product.id}`} className="font-display text-lg sm:text-xl leading-snug text-[#2C2B29] hover:text-[#5B7B53] transition-colors line-clamp-2">
          {product.title}
        </Link>
        <div className="flex items-center gap-2 mt-1">
          <Rating value={product.rating} />
          <span className="text-xs text-[#5C5A56]">{product.rating} ({product.review_count.toLocaleString()})</span>
        </div>
        <div className="hidden sm:block"><BenefitList items={product.benefits} testid={`top-pick-benefits-${rank}`} /></div>
        {product.benefits?.[0] && <p className="sm:hidden text-xs text-[#465F41] mt-1.5 line-clamp-1">✓ {product.benefits[0]}</p>}
        <div className="mt-auto pt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="text-lg font-semibold text-[#2C2B29]">{product.price}</span>
          <BuyButton product={product} testid={`top-pick-buy-${rank}`} className="!px-3 sm:!px-4 !py-2 text-xs sm:text-sm" />
        </div>
      </div>
    </article>
  );
}

export function TopPicksSection({ products }) {
  if (!products.length) return null;
  return (
    <section id="top-picks" data-testid="top-picks-section" className="max-w-7xl mx-auto px-6 lg:px-10 py-10 sm:py-16 scroll-mt-20">
      <p className="text-xs uppercase tracking-[0.2em] text-[#D17A58] mb-2 font-semibold">Ranked by our editors</p>
      <h2 className="font-display text-3xl sm:text-5xl text-[#2C2B29] mb-3">Today's top picks.</h2>
      <p className="text-sm sm:text-base text-[#5C5A56] max-w-2xl mb-6 sm:mb-10">The best-rated, best-value home & kitchen buys on Amazon.in right now — chosen on reviews, reliability and price.</p>
      <div className="grid lg:grid-cols-2 gap-3 sm:gap-5">
        {products.map((p, i) => <TopPickRow key={p.id} product={p} rank={i + 1} />)}
      </div>
    </section>
  );
}
